import { useEffect, useMemo, useState } from 'react';
import { Values } from '@lib/interfaces/form';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import { useModalTableAllTabManager } from '@lib/components/ModalTableMultiselect/ModalTableMultiselect';
import useModalTableModeManager from '@lib/components/ModalTableMultiselect/hooks/useModalTableModeManager';
import useSelectedFormFieldIds from '@lib/components/ModalTableMultiselect/hooks/useSelectedFormFieldIds';
import { ModalTableMultiselectMode } from '@lib/components/ModalTableMultiselect/enums';

type Args<TFilter> = {
  isOpen: boolean;
  mode?: ModalTableMultiselectMode;
  formRef: FormRefType;
  fieldName: string;
  selectedIdsOverride?: string[];
  filterStateDefault: Values;
  filterOverrides?: Values;
  selectionLimit?: number;
  baseFilters?: TFilter[];
  // Must be stable (module-level).
  createAdditionalFilters: (filterValues: Values) => TFilter[];
};

// Tab/filter/selection state shared by the Tasks and Task completions
// pickers; each one only adds its own queries and columns.
export default function useTaskPickerState<TItem, TFilter>({
  isOpen,
  mode = ModalTableMultiselectMode.WithTabs,
  formRef,
  fieldName,
  selectedIdsOverride,
  filterStateDefault,
  filterOverrides,
  selectionLimit,
  baseFilters,
  createAdditionalFilters,
}: Args<TFilter>) {
  const [isFetchingAllIds, setIsFetchingAllIds] = useState(false);

  const selectedIdsFromForm = useSelectedFormFieldIds(formRef, fieldName);
  const selectedIds = selectedIdsOverride ?? selectedIdsFromForm;

  const modeState = useModalTableModeManager({
    isOpen,
    mode,
    selectedIds,
    filterStateDefault,
    filterOverrides,
    selectionLimit,
  });
  const { activeTab, filterState, filterStateSelected, isSelectedOnlyMode } =
    modeState;

  const allTabState = useModalTableAllTabManager<TItem>({
    isOpen,
    mode,
    activeTab,
    isSelectedOnlyMode,
  });

  // Keyed on the filter values, not the `[value, setter]` tuples — those are
  // fresh arrays every render and would defeat the memo.
  const hasFilterState = !!filterState;
  const filterValues = filterState?.[0];
  const filterValuesSelected = filterStateSelected[0];

  const additionalFilters = useMemo(() => {
    if (isSelectedOnlyMode || !hasFilterState) return undefined;
    return [
      ...createAdditionalFilters(filterValues || {}),
      ...(baseFilters || []),
    ];
  }, [
    filterValues,
    hasFilterState,
    isSelectedOnlyMode,
    baseFilters,
    createAdditionalFilters,
  ]);

  const additionalFiltersSelected = useMemo(
    () => [
      ...createAdditionalFilters(filterValuesSelected || {}),
      ...(baseFilters || []),
    ],
    [filterValuesSelected, baseFilters, createAdditionalFilters],
  );

  return {
    ...modeState,
    ...allTabState,
    selectedIds,
    additionalFilters,
    additionalFiltersSelected,
    isFetchingAllIds,
    setIsFetchingAllIds,
  };
}

// Feeds the All tab query result into the all-tab manager and returns what
// the All tab should show (live, or the cached result while another tab is
// active). Pass the collection in its final row shape — it's what gets cached.
export function useAllTabSnapshot<TItem>(
  {
    handleAllTabQueryResult,
    getAllTabSnapshot,
  }: Pick<
    ReturnType<typeof useModalTableAllTabManager<TItem>>,
    'handleAllTabQueryResult' | 'getAllTabSnapshot'
  >,
  {
    collection,
    totalCount,
    loading,
    firstLoading,
  }: {
    collection: TItem[];
    totalCount: number;
    loading: boolean;
    firstLoading: boolean;
  },
) {
  useEffect(() => {
    handleAllTabQueryResult({ collection, totalCount, loading, firstLoading });
  }, [collection, totalCount, loading, firstLoading, handleAllTabQueryResult]);

  return getAllTabSnapshot({ collection, totalCount, firstLoading });
}
